import React, { useCallback, useMemo, useRef, useEffect } from "react";
import { useOrderbook } from "../../hooks/useOrderbook";
import { formatPrice, formatSize } from "../../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  StatusIndicator,
  LayoutHeader,
} from "../smartlayout/fragments";
import {
  DEFAULT_HEIGHT_ORDERBOOK,
  DEFAULT_MAX_DEPTH,
} from "../../constants";
import "../smartlayout/layout.css";
import { AskLevelFragment, BidLevelFragment } from "./OrderbookLevelFragments";
import { SubscriptionClient } from "../../sdk";

interface Orderbook001Props {
  baseURL: string;
  subscriptionClient: SubscriptionClient;
  marketId: string;
  maxDepth?: number;
  height?: string;
  onPriceClick?: (price: number) => void;
}

function Orderbook001({
  baseURL,
  subscriptionClient,
  marketId,
  maxDepth = DEFAULT_MAX_DEPTH,
  height = DEFAULT_HEIGHT_ORDERBOOK,
  onPriceClick,
}: Orderbook001Props): JSX.Element {
  const { asks, bids, spread, midPrice, loading, error, isConnected, wsError } = useOrderbook({
    baseURL,
    subscriptionClient,
    marketId,
    maxDepth,
  });

  // Refs for scrolling
  const askSectionRef = useRef<HTMLDivElement>(null);
  const bidSectionRef = useRef<HTMLDivElement>(null);
  const scrollScheduledRef = useRef<number | null>(null);
  const lastAsksLengthRef = useRef<number>(0);
  const lastBidsLengthRef = useRef<number>(0);

  // Optimized scroll: batch operations using requestAnimationFrame
  // Only triggers when orderbook data actually changes (length changes)
  useEffect(() => {
    const asksChanged = asks.length !== lastAsksLengthRef.current;
    const bidsChanged = bids.length !== lastBidsLengthRef.current;
    if (!asksChanged && !bidsChanged) {
      return;
    }

    // Update refs
    lastAsksLengthRef.current = asks.length;
    lastBidsLengthRef.current = bids.length;

    // Cancel any pending scroll operation
    if (scrollScheduledRef.current !== null) {
      cancelAnimationFrame(scrollScheduledRef.current);
    }

    // Schedule scroll operations in the next animation frame
    // This batches DOM operations and prevents layout thrashing
    scrollScheduledRef.current = requestAnimationFrame(() => {
      // Scroll ask section to bottom
      if (askSectionRef.current && asksChanged) {
        const askEl = askSectionRef.current;
        // Direct assignment is faster than scrollIntoView
        askEl.scrollTop = askEl.scrollHeight;
      }
      // Scroll bid section to top
      if (bidSectionRef.current && bidsChanged) {
        const bidEl = bidSectionRef.current;
        // Direct assignment is faster than scrollIntoView
        bidEl.scrollTop = 0;
      }
      scrollScheduledRef.current = null;
    });

    // Cleanup: cancel animation frame on unmount
    return () => {
      if (scrollScheduledRef.current !== null) {
        cancelAnimationFrame(scrollScheduledRef.current);
        scrollScheduledRef.current = null;
      }
    };
  }, [asks.length, bids.length]);

  // Calculate max cumulative for depth visualization
  // Both asks and bids are descending (last element has highest cumulative)
  // Optimized: only depend on last elements, not entire arrays
  const lastAskCumulative =
    asks.length > 0
      ? (asks[asks.length - 1]?.cumulative ?? 0)
      : 0;
  const lastBidCumulative =
    bids.length > 0
      ? (bids[bids.length - 1]?.cumulative ?? 0)
      : 0;

  const maxCumulative = useMemo(
    () => Math.max(lastAskCumulative, lastBidCumulative),
    [lastAskCumulative, lastBidCumulative],
  );

  // Format functions are pure - no need for useCallback
  const formatPriceMemo = formatPrice;
  const formatSizeMemo = formatSize;

  const getDepthPercentage = useCallback(
    (depthValue: number): number => {
      if (maxCumulative === 0) return 0;
      return (depthValue / maxCumulative) * 100;
    },
    [maxCumulative],
  );

  if (loading) {
    return <LoadingMessage message="Loading orderbook..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* WebSocket Error Notice */}
      {wsError && (asks.length > 0 || bids.length > 0) && (
        <div className="orderbook-ws-warning">
          <img
            src="/caution-icon.svg"
            alt="Caution"
            className="orderbook-ws-warning-icon"
          />
          <span className="orderbook-ws-warning-text">{wsError}</span>
        </div>
      )}

      {/* Header */}
      <LayoutHeader title={<StatusIndicator isConnected={isConnected} />}>
        <div>Depth: {maxDepth}</div>
      </LayoutHeader>

      {/* Mid Price / Spread */}
      {midPrice && (
        <div className="orderbook-mid-price">
          <div className="orderbook-mid-price-value">
            {formatPriceMemo(midPrice)}
          </div>
          {spread !== undefined && (
            <div className="orderbook-mid-price-spread">
              Spread: {formatPriceMemo(spread)}
            </div>
          )}
        </div>
      )}

      {/* Orderbook Content */}
      <div className="orderbook-content">
        {/* Asks: depth = cumulative from this level down to best ask (long at top, short at bottom) */}
        <div className="orderbook-section" ref={askSectionRef}>
          {asks.map((level, index) => {
            const askDepthValue =
              lastAskCumulative - level.cumulative + level.size;
            const depthPercent = getDepthPercentage(askDepthValue);
            return (
              <AskLevelFragment
                key={`ask-${level.price}-${index}`}
                level={level}
                index={index}
                isLast={index === asks.length - 1}
                depthPercent={depthPercent}
                formatPrice={formatPriceMemo}
                formatSize={formatSizeMemo}
                onPriceClick={onPriceClick}
              />
            );
          })}
          {asks.length === 0 && (
            <EmptyMessage message="No ask orders" />
          )}
        </div>

        {/* Bids (Buy Orders) - Bottom Section */}
        <div className="orderbook-section bids" ref={bidSectionRef}>
          {bids.map((level, index) => {
            const depthPercent = getDepthPercentage(level.cumulative);
            return (
              <BidLevelFragment
                key={`bid-${level.price}-${index}`}
                level={level}
                index={index}
                isLast={index === bids.length - 1}
                depthPercent={depthPercent}
                formatPrice={formatPriceMemo}
                formatSize={formatSizeMemo}
                onPriceClick={onPriceClick}
              />
            );
          })}
          {bids.length === 0 && (
            <EmptyMessage message="No bid orders" />
          )}
        </div>
      </div>

      {/* Footer - Market Depth Summary */}
      <FooterSummary
        totalBids={lastBidCumulative}
        totalAsks={lastAskCumulative}
      />
    </div>
  );
}

// Memoized footer component to prevent recalculation
const FooterSummary = React.memo(
  ({ totalBids, totalAsks }: { totalBids: number; totalAsks: number }) => {
    const total = totalBids + totalAsks;
    const bidsPercent =
      total > 0 ? `${((totalBids / total) * 100).toFixed(0)}%` : "0%";
    const asksPercent =
      total > 0 ? `${((totalAsks / total) * 100).toFixed(0)}%` : "0%";

    return (
      <div className="orderbook-footer">
        <div className="orderbook-footer-legend">
          <div className="orderbook-footer-bar bid" />
          <span className="color-text-muted">{bidsPercent}</span>
        </div>
        <div className="orderbook-footer-legend">
          <div className="orderbook-footer-bar ask" />
          <span className="color-text-muted">{asksPercent}</span>
        </div>
      </div>
    );
  },
);

FooterSummary.displayName = "FooterSummary";

export default React.memo(Orderbook001);
